import { Badge, Button, Card, EmptyState, SegmentedControl, Table, type Column } from "@helmr/ui";
import { useInfiniteQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { listStaff, type StaffMember } from "../../api/staff";
import { NoAccess } from "../../app/NoAccess";
import { areaAllows } from "../../app/nav";
import { useServices } from "../../app/services";
import { Failure, RowsSkeleton, styles as c, useScope } from "../classes/common";
import { useRoleNames } from "./roles";

export function StaffList() {
  const { client } = useServices();
  const sc = useScope();
  const nav = useNavigate();
  const roles = useRoleNames();
  const [status, setStatus] = useState<"active" | "inactive">("active");
  const q = useInfiniteQuery({
    queryKey: ["staff", ...sc.key, "list", status],
    queryFn: ({ pageParam }) => listStaff(client, { status, cursor: pageParam }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    retry: false, enabled: areaAllows("staff", sc.ctx.has),
  });
  if (sc.ctx.status !== "loading" && !areaAllows("staff", sc.ctx.has)) return <NoAccess />;
  const rows = q.data?.pages.flatMap((p) => p.staff) ?? [];
  const branch = (id: string | null) => sc.ctx.me?.branches.find((b) => b.id === id)?.name ?? "—";
  const cols: Column<StaffMember>[] = [
    { key: "name", header: "Name", cell: (m) => <Link to={`/staff/${m.id}`} className={c.trunc} title={m.name} onClick={(e) => e.stopPropagation()}>{m.name}</Link> },
    { key: "role", header: "Role", cell: (m) => roles.nameOf(m.roleId) ?? "—" },
    { key: "branch", header: "Branch", cell: (m) => branch(m.homeBranchId) },
    { key: "phone", header: "Phone", cell: (m) => m.phone },
    { key: "status", header: "Status", cell: (m) => <Badge tone={m.status === "active" ? "ok" : "neutral"}>{m.status === "active" ? "Active" : "Removed"}</Badge> },
  ];
  return (
    <div className={c.page}>
      <div className={c.head}>
        <div><h1>Staff</h1><p>Coaches, desk and managers.</p></div>
        <div className={c.tools}>
          <Button as={Link} to="/me/attendance">My attendance</Button>
          {sc.ctx.has("staff_attendance.view") ? <Button as={Link} to="/staff/attendance">Attendance</Button> : null}
          {sc.ctx.has("staff.add") ? <Button as={Link} to="/staff/new" variant="primary">Add staff</Button> : null}
        </div>
      </div>
      <SegmentedControl label="Show" value={status} onChange={setStatus} options={[{ value: "active", label: "Active" }, { value: "inactive", label: "Removed" }]} />
      <Card aria-label="Staff list">
        {q.isPending ? <RowsSkeleton /> : q.error ? <Failure error={q.error} title="Could not load staff" onRetry={() => void q.refetch()} /> : rows.length === 0 ? (
          <EmptyState icon="badge" title={status === "active" ? "No staff yet" : "No one has been removed"} description={status === "active" ? "Add the people who work at this gym." : undefined} action={status === "active" && sc.ctx.has("staff.add") ? <Button as={Link} to="/staff/new" variant="primary">Add staff</Button> : undefined} />
        ) : (
          <Table label="Staff" columns={cols} rows={rows} rowKey={(m) => m.id} onRowClick={(m) => nav(`/staff/${m.id}`)} minWidth={560} hasMore={!!q.hasNextPage} onLoadMore={() => void q.fetchNextPage()} loadingMore={q.isFetchingNextPage} summary={`${rows.length} ${rows.length === 1 ? "person" : "people"}`} />
        )}
      </Card>
    </div>
  );
}
